'use client'

import Box from '@mui/material/Box'

import Assessment from '@/components/Assessment'
import { BlogPreview } from '@/components/blogSlices/BlogPreview'
import Certificate from '@/components/Certificate'
import Contributions from '@/components/Contributions'
import CuratePortfolios from '@/components/CuratePortfolios'
import DownloadGuide from '@/components/DownloadGuide'
import GenericHero from '@/components/Hero/GenericHero'
import LearnMoreTiles from '@/components/LearnMoreTiles'
import ProjectFunnel from '@/components/ProjectFunnel/ProjectFunnel'
import RequestDemo from '@/components/RequestDemo'
import ResearchCustomerStory from '@/components/ResearchCustomerStory'
import TwoColumnsWithSquareImage from '@/components/TwoColumnsWithSquareImage'
import FundClimateProjectsLogos from '@/sections/FundClimateProjectsLogos'
import MoominCustomerStory from '@/sections/MoominCustomerStory'
import VestreCustomerStory from '@/sections/VestreCustomerStory'
import { HUBSPOT_FORM_IDS } from '@/utills/constants'
import { RenderOn } from '@/utills/DeviceUtils'

export default function Page() {
    return (
        <>
            <Box>
                <GenericHero
                    chips={{
                        items: [
                            {
                                src: '/images/heros/chip-fund-climate-projects-1.png',
                                width: 395,
                                height: 72,
                            },
                            {
                                src: '/images/heros/chip-fund-climate-projects-2.png',
                                width: 384,
                                height: 72,
                            },
                            {
                                src: '/images/heros/chip-fund-climate-projects-3.png',
                                width: 384,
                                height: 72,
                                sx: {
                                    ml: 9,
                                },
                            },
                            {
                                src: '/images/heros/chip-fund-climate-projects-4.png',
                                width: 406,
                                height: 72,
                            },
                        ],
                        gap: 9,
                    }}
                    backgroundImageMobile={'/images/heros/fund-climate-projects-mobile.png'}
                    title={
                        <>
                            Fund climate projects{' '}
                            <RenderOn breakPoints={['xl', 'lg']}>
                                <br />
                            </RenderOn>{' '}
                            you can trust
                        </>
                    }
                    description={
                        'The voluntary carbon markets are like the wild west — too often, good intentions can fall short. But when done correctly carbon credits are the single best mechanism to address climate change, biodiversity loss, and humanitarian crises. '
                    }
                    button={{
                        href: '/request-a-demo',
                        label: 'Request a demo',
                    }}
                />
                <FundClimateProjectsLogos />
            </Box>
            <LearnMoreTiles
                tiles={[
                    {
                        title: (
                            <>
                                High quality and <br /> peace of mind
                            </>
                        ),
                        description: 'Fund climate projects rigorously vetted by experts.',
                        scrollToSection: 'impact-session',
                        icon: '/icons/icon-high-quality-peace.png',
                    },
                    {
                        title: 'Transparency at every level',
                        description: 'See where your contributions go.',
                        scrollToSection: 'contributions',
                        icon: '/icons/icon-transparency.png',
                    },
                    {
                        title: 'Easy-to-use platform',
                        description:
                            'Cultivate authentic company-aligned climate project portfolios.',
                        scrollToSection: 'curate-portfolios',
                        icon: '/icons/icon-easy-platform.png',
                    },
                    {
                        title: 'Shareable insights',
                        description:
                            'Get stakeholder buy-in and enhance your sustainability report.',
                        scrollToSection: 'share-impact',
                        icon: '/icons/icon-shareable.png',
                    },
                ]}
            />
            <MoominCustomerStory />
            <Box id={'impact-session'}>
                <Assessment />
            </Box>
            <ProjectFunnel />
            <TwoColumnsWithSquareImage
                title={'Accelerate net zero goals'}
                paragraph={`Meet standards set by the Science Based Target Initiative (SBTi) which expects companies to go beyond value chain mitigation by funding high quality climate projects that comply with the The Core Carbon Principles and Assessment Framework set out by the Integrity Council for the Voluntary Carbon Market (iCVCM).`}
                image={{
                    src: '/images/logo-grids/logos-sbti.png',
                    alt: 'Accelerate net zero goals logos',
                }}
                snippet={
                    <BlogPreview
                        title={
                            'A guide to making credible green claims: from carbon neutral to net zero and beyond'
                        }
                        url={
                            '/blog/making-credible-green-claims-from-carbon-neutral-to-net-zero-and-beyond'
                        }
                    />
                }
            />
            <DownloadGuide hubspotFormId={HUBSPOT_FORM_IDS.greenForm} />
            <Box id={'contributions'}>
                <Contributions />
            </Box>
            <ResearchCustomerStory
                customTag={'NEW IN LUNE'}
                mobileBackgroundImage={'/images/project-impact-maps-mobile.png'}
                backgroundImage={'/images/project-impact-maps-desktop.png'}
                title={<>Project impact maps: A new window into nature-based projects</>}
                light={true}
                button={{
                    label: 'Learn more',
                    href: '/blog/new-project-impact-maps',
                }}
                reversedOnMobile
            />
            <Box id={'curate-portfolios'}>
                <CuratePortfolios />
            </Box>
            <VestreCustomerStory />
            <Box id={'share-impact'}>
                <Certificate
                    text={
                        "Get stakeholder buy-in and elevate your sustainability reporting by seamlessly integrating Lune's impact summaries, official retirement certificates, and dedicated sustainability page into your climate legacy."
                    }
                />
            </Box>
            <ResearchCustomerStory
                mobileBackgroundImage={'/images/customer-quotes/voluntary-buyers-mobile.png'}
                backgroundImage={'/images/customer-quotes/voluntary-buyers-desktop.png'}
                title={<>Voluntary carbon buyers decarbonise 2× as fast as their peers</>}
                light={true}
                button={{
                    label: 'Learn more',
                    href: '/blog/8-stats-that-show-climate-leaders-are-buying-carbon-credits/',
                }}
            />
            <RequestDemo />
        </>
    )
}
